import { useCallback, useRef } from 'react';

// Pointer events fire for all input types (mouse, touch, pen). Browsers
// handle touch and pen scrolling natively, so JS drag logic is only needed
// for mouse.
export default function useDragScroll(ref) {
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const didDrag = useRef(false);

  const onPointerDown = useCallback(
    (e) => {
      if (e.pointerType !== 'mouse') {
        return;
      }
      // Prevents native drag of ancestor elements (e.g. a link wrapping the
      // card), which would swallow the subsequent pointermove events our
      // scroll logic depends on.
      e.preventDefault();
      isDragging.current = true;
      didDrag.current = false;
      dragStartX.current = e.clientX;
      dragScrollLeft.current = ref.current.scrollLeft;
      // Disable scroll-snap during drag so scrollLeft tracks the cursor
      // freely. Restored on pointerup, which triggers snap-to-nearest-slide.
      ref.current.style.scrollSnapType = 'none';
      // Stop any in-progress smooth scroll from a previous drag. Without this,
      // the ongoing animation can trigger a pointercancel on the new drag,
      // which would incorrectly clear isDragging via onPointerCancel.
      ref.current.scrollTo({
        left: ref.current.scrollLeft,
        behavior: 'instant',
      });
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [ref]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!isDragging.current || e.pointerType !== 'mouse') {
        return;
      }
      // Safety net: if no mouse button is held, we somehow missed the pointerup.
      // Clean up rather than continuing to move the carousel.
      if (e.buttons === 0) {
        isDragging.current = false;
        ref.current.style.scrollSnapType = '';
        return;
      }
      const dx = e.clientX - dragStartX.current;
      // 4px dead zone: prevents a slight mouse tremor during a click from
      // being treated as a drag, which would suppress the click event.
      if (Math.abs(dx) > 4) {
        didDrag.current = true;
      }
      ref.current.scrollLeft = dragScrollLeft.current - dx;
    },
    [ref]
  );

  const onPointerUp = useCallback(
    (e) => {
      if (e.pointerType !== 'mouse') {
        return;
      }
      isDragging.current = false;
      if (!didDrag.current) {
        ref.current.style.scrollSnapType = '';
        return;
      }
      // Animate to the nearest slide, then restore scroll-snap. We can't
      // restore snap first because that causes a hard jump with no easing.
      const containerWidth = ref.current.offsetWidth;
      const nearestSlide = Math.round(ref.current.scrollLeft / containerWidth);
      ref.current.scrollTo({
        left: nearestSlide * containerWidth,
        behavior: 'smooth',
      });
      // Guard against the case where a new drag starts before the animation
      // finishes — restoring snap mid-drag would re-introduce the hard-snap.
      const restoreSnapType = () => {
        if (!isDragging.current && ref.current) {
          ref.current.style.scrollSnapType = '';
        }
      };
      ref.current.addEventListener('scrollend', restoreSnapType, {
        once: true,
      });
      // Fallback for browsers without scrollend support, and for no-op scrolls
      // (e.g. dragging past the first/last slide) where scrollend won't fire.
      setTimeout(restoreSnapType, 400);
      // Suppress the click that fires after a drag. onClickCapture on the
      // container isn't sufficient because the anchor wrapping the card sits
      // above the container in the tree and has already been visited by the
      // capture phase before we can stop propagation. A window-level capture
      // listener fires before any element and handles all DOM structures.
      window.addEventListener(
        'click',
        (clickEvent) => {
          clickEvent.stopPropagation();
          clickEvent.preventDefault();
        },
        { capture: true, once: true }
      );
      didDrag.current = false;
    },
    [ref]
  );

  // Handles cases where pointerup doesn't fire (e.g. mouse released outside
  // the browser window), which would otherwise leave isDragging stuck as true.
  const onPointerCancel = useCallback(
    (e) => {
      if (e.pointerType !== 'mouse') {
        return;
      }
      isDragging.current = false;
      didDrag.current = false;
      if (ref.current) {
        ref.current.style.scrollSnapType = '';
      }
    },
    [ref]
  );

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
